import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { ExportStatus } from 'api/export/types';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { triggerFileDownload } from 'utils/dom';
// Module-level dedup: tracks exports handled by a per-page listener so the
// global fallback skips them. Capped at 100 entries (oldest evicted) so
// long-lived sessions don't accumulate IDs without bound.
const MAX_HANDLED_IDS = 100;
const handledExportIds = new Set();
export const markExportHandled = (id)=>{
    if (handledExportIds.size >= MAX_HANDLED_IDS) {
        const oldest = handledExportIds.values().next().value;
        if (oldest !== undefined) handledExportIds.delete(oldest);
    }
    handledExportIds.add(id);
};
export const isExportAlreadyHandled = (id)=>handledExportIds.has(id);
// Signals that the in-flight export is email-delivery. Consumed by
// GlobalAsyncExportListener to show email-specific copy.
let emailExportPending = false;
export const markEmailExportPending = ()=>{
    emailExportPending = true;
};
export const consumeEmailExportPending = ()=>{
    const val = emailExportPending;
    emailExportPending = false;
    return val;
};
const SUCCESS_TOAST_DURATION_SECONDS = 15;
export const useAsyncExport = ({ onExportCompleted, isExportInProgress, persistSuccessToast = false, isEmailExport = false })=>{
    const translate = useTranslate();
    const handleExport = useCallback((response)=>{
        logger.log('async-create ~ socketData', response);
        const data = JSON.parse(response.data);
        if (!data || !data.downloadUrl || data.status !== ExportStatus.Completed) {
            toastService({
                title: translate('async.exports.error.title'),
                message: translate('async.exports.error.message')
            }).error();
            onExportCompleted();
            return;
        }
        const { downloadUrl } = data;
        if (isEmailExport) {
            // Global listener owns the notification; signal caller to close modal.
            onExportCompleted();
            return;
        }
        if (!isExportInProgress) {
            return;
        }
        if (data.exportsMetaDataId && isExportAlreadyHandled(data.exportsMetaDataId)) {
            return;
        }
        if (data.exportsMetaDataId) {
            markExportHandled(data.exportsMetaDataId);
        }
        triggerFileDownload(downloadUrl);
        toastService({
            title: translate('async.exports.success.title'),
            message: /*#__PURE__*/ _jsxs("p", {
                children: [
                    translate('async.exports.success.message'),
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx("a", {
                        href: downloadUrl,
                        download: true,
                        children: translate('download.file')
                    })
                ]
            }),
            duration: persistSuccessToast ? 0 : SUCCESS_TOAST_DURATION_SECONDS
        }).success();
        onExportCompleted();
    }, [
        isEmailExport,
        isExportInProgress,
        onExportCompleted,
        persistSuccessToast,
        translate
    ]);
    return {
        handleExport
    };
};
